/**
 * 路由相关的类型定义
 */

import type { RouteRecordRaw } from 'vue-router'

/**
 * 扩展的路由元信息接口
 */
export interface ExtendedRouteMeta {
  /** 页面标题 */
  title?: string
  /** 是否需要认证 */
  requiresAuth?: boolean
  /** 已登录用户是否重定向 */
  redirectIfAuth?: boolean
  /** 是否允许未完成问卷的用户访问 */
  allowWithoutQuestionnaire?: boolean
  /** 页面描述 */
  description?: string
  /** 页面图标 */
  icon?: string
  /** 是否在导航中隐藏 */
  hideInNav?: boolean
  /** 页面权限要求 */
  permissions?: string[]
  /** 页面分组 */
  group?: 'auth' | 'main' | 'personal' | 'trading' | 'admin'
}

/**
 * 扩展的路由记录接口
 */
export interface ExtendedRouteRecord extends Omit<RouteRecordRaw, 'meta'> {
  meta?: ExtendedRouteMeta
}

/**
 * 路由导航结果
 */
export interface NavigationResult {
  /** 是否允许导航 */
  allowed: boolean
  /** 重定向路径（如果需要） */
  redirectTo?: string
  /** 错误信息 */
  error?: string
}

/**
 * 页面权限检查结果
 */
export interface PermissionCheckResult {
  /** 是否有权限 */
  hasPermission: boolean
  /** 重定向路径 */
  redirectTo?: string
  /** 原因说明 */
  reason?: string
}

/**
 * 路由守卫配置
 */
export interface RouteGuardConfig {
  /** 认证检查超时时间 */
  authTimeout?: number
  /** 是否启用余额刷新 */
  enableBalanceRefresh?: boolean
  /** 需要刷新余额的页面列表 */
  balanceRequiredPages?: string[]
}

/**
 * 个人中心相关路由名称
 */
export const PERSONAL_CENTER_ROUTES = {
  PERSONAL_CENTER: 'PersonalCenter',
  ORDER_RECORD: 'OrderRecord',
  ACCOUNT_SETTINGS: 'AccountSettings'
} as const

/**
 * 认证相关路由名称
 */
export const AUTH_ROUTES = {
  LOGIN: 'Login',
  REGISTER: 'Register',
  FORGOT_PASSWORD: 'ForgotPassword',
  RESET_PASSWORD: 'ResetPassword'
} as const

/**
 * 主要功能路由名称
 */
export const MAIN_ROUTES = {
  HOME: 'Home',
  DASHBOARD: 'Dashboard',
  QUESTIONNAIRE: 'Questionnaire',
  QUESTIONNAIRE_RESULTS: 'QuestionnaireResults',
  CARBON_FOOTPRINT: 'CarbonFootprint'
} as const

/**
 * 交易相关路由名称
 */
export const TRADING_ROUTES = {
  PROJECT_TYPES: 'ProjectTypes',
  PROJECT_LIST: 'ProjectList',
  REGULAR_PROJECT_DETAIL: 'RegularProjectDetail',
  PRE_SALE_PROJECT_DETAIL: 'PreSaleProjectDetail',
  BUYER_LIST: 'BuyerList',
  TRADING: 'Trading',
  TRANSACTION_SUCCESS: 'TransactionSuccess',
  TRANSACTION_HISTORY: 'TransactionHistory'
} as const

/**
 * 所有路由名称的联合类型
 */
export type RouteNames = 
  | typeof PERSONAL_CENTER_ROUTES[keyof typeof PERSONAL_CENTER_ROUTES]
  | typeof AUTH_ROUTES[keyof typeof AUTH_ROUTES]
  | typeof MAIN_ROUTES[keyof typeof MAIN_ROUTES]
  | typeof TRADING_ROUTES[keyof typeof TRADING_ROUTES]
  | 'UserChat'
  | 'SimpleDebug'
  | 'SetPaymentPassword'
  | 'StartProjects'
  | 'CarbonCreditBuyers'
  | 'NotFound'